import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Shield, CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const { getAuthHeader, refreshSubscription, isAuthenticated } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !isAuthenticated) {
      navigate("/pricing");
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/checkout/status/${sessionId}`,
          { headers: getAuthHeader() }
        );

        if (res.data.payment_status === "paid") {
          setStatus("success");
          await refreshSubscription();
        } else if (res.data.status === "expired") {
          setStatus("error");
        } else {
          // Continue polling
          if (attempts < 10) {
            setTimeout(() => setAttempts(a => a + 1), 2000);
          } else {
            setStatus("error");
          }
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts, isAuthenticated]);

  return (
    <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-6">
      <div className="glass-card rounded-xl p-8 max-w-md w-full text-center">
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
            <Shield className="text-white" size={24} />
          </div>
          <span className="font-heading font-bold text-xl text-white">ServeurOnline</span>
        </Link>

        {status === "checking" && (
          <>
            <Loader2 className="h-16 w-16 text-indigo-500 mx-auto mb-4 animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Verification du paiement...
            </h1>
            <p className="text-slate-400">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Paiement reussi !
            </h1>
            <p className="text-slate-400 mb-6">
              Votre abonnement est maintenant actif. Vous pouvez commencer a utiliser ServeurOnline.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Acceder au dashboard
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Erreur de paiement
            </h1>
            <p className="text-slate-400 mb-6">
              Le paiement n&apos;a pas pu etre confirme. Veuillez reessayer ou contacter le support.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate("/pricing")}
                variant="secondary"
                className="bg-slate-800 hover:bg-slate-700"
              >
                Retour aux tarifs
              </Button>
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
